import React, { useState } from 'react';
import { Field, ErrorMessage } from 'formik';
import FileUploadField from './FileUploadField';
// import SignatureCanvas from 'react-signature-canvas';

const FormField = ({ field, value, setFieldValue, error, touched, readOnly }) => {
  const [sigPad, setSigPad] = useState(null);

  const isEmptyValue = value === undefined || value === null || value === '' ||
    (Array.isArray(value) && value.length === 0);

  // Format the value for read-only (view-only) display.
  const getReadOnlyDisplay = () => {
    if (field.type === 'date') {
      const d = new Date(value);
      return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-GB');
    }
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    return value;
  };

  const clearSignature = () => {
    if (sigPad) {
      sigPad.clear();
      setFieldValue(field.name, '');
    }
  };

  const saveSignature = () => {
    if (sigPad && !sigPad.isEmpty()) {
      const signatureData = sigPad.toDataURL();
      setFieldValue(field.name, signatureData);
    }
  };

  const renderField = () => {
    // View-only display for prefilled (locked) fields. Formik value is left
    // untouched so it still submits.
    if (readOnly && !isEmptyValue) {
      return <div className="form-readonly-value">{getReadOnlyDisplay()}</div>;
    }

    switch (field.type) {
      case 'text':
      case 'email':
        return (
          <Field
            type={field.type}
            name={field.name}
            placeholder={field.label}
            className={`form-input ${touched && error ? 'error' : ''}`}
          />
        );
      case 'number':
        return (
          <Field
            type={field.type}
            name={field.name}
            placeholder={field.label}
            className={`form-input ${touched && error ? 'error' : ''}`}
            onWheel={field.type === 'number' ? (e) => e.target.blur() : undefined}
          />
        );

      case 'date':
        return (
          <Field
            type="date"
            name={field.name}
            className={`form-input ${touched && error ? 'error' : ''}`}
          />
        );

      case 'textarea':
        return (
          <Field
            as="textarea"
            name={field.name}
            placeholder={field.label}
            className={`form-textarea ${touched && error ? 'error' : ''}`}
            rows={4}
          />
        );

      case 'select':
        return (
          <Field
            as="select"
            name={field.name}
            className={`form-select ${touched && error ? 'error' : ''}`}
          >
            <option value="">Select {field.label}</option>
            {field.options.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </Field>
        );

      case 'radio':
        return (
          <div className="radio-group">
            {field.options.map((option, index) => (
              <label key={index} className="radio-label">
                <Field
                  type="radio"
                  name={field.name}
                  value={option}
                  className="radio-input"
                  onChange={() => setFieldValue(field.name, option)}
                />
                <span className="radio-text">{option}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <label className="checkbox-label">
            <Field
              type="checkbox"
              name={field.name}
              className="checkbox-input"
            />
            <span className="checkbox-text">{field.label}</span>
          </label>
        );

      case 'fileUpload':
        return (
          <FileUploadField
            field={field}
            value={value}
            setFieldValue={setFieldValue}
            error={error}
            touched={touched}
          />
        );

      case 'signature':
        return (
          <div className="signature-field">
            <div className="signature-container">
              {/* <SignatureCanvas
                ref={(ref) => setSigPad(ref)}
                penColor="black"
                canvasProps={{
                  width: 500,
                  height: 200,
                  className: 'signature-canvas'
                }}
              /> */}
            </div>
            <div className="signature-actions">
              <button
                type="button"
                className="btn-clear"
                onClick={clearSignature}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn-save"
                onClick={saveSignature}
              >
                Save
              </button>
            </div>
            {value && (
              <div className="signature-preview">
                <p>Signature saved</p>
              </div>
            )}
          </div>
        );

      case 'multiselect':
        return (
          <div className="checkbox-group">
            {field.options.map((option, index) => (
              <label key={index} className="checkbox-label">
                <Field
                  type="checkbox"
                  name={field.name}
                  value={option}
                  checked={value?.includes(option)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const newValue = [...(value || [])];

                    if (checked) {
                      // If "Nil" is selected, reset to just "Nil"
                      if (option === "Nil") {
                        setFieldValue(field.name, ["Nil"]);
                      } else {
                        // Remove "Nil" if present
                        const filtered = newValue.filter((item) => item !== "Nil");
                        setFieldValue(field.name, [...filtered, option]);
                      }
                    } else {
                      const updated = newValue.filter((item) => item !== option);
                      setFieldValue(field.name, updated);
                    }
                  }}
                  className="checkbox-input"
                />
                <span className="checkbox-text">{option}</span>
              </label>
            ))}
          </div>
        );


      default:
        return <Field name={field.name} />;
    }
  };

  return (
    <div className={`form-field ${field.type}-field`}>
      {field.type !== 'checkbox' && (
        <label className="field-label" htmlFor={field.name}>
          {field.label} {field.required && <span className="required">*</span>}
        </label>
      )}

      {renderField()}

      <ErrorMessage
        name={field.name}
        component="div"
        className="error-message"
      />
    </div>
  );
};

export default FormField;
